import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useThemeContext } from '../../../../hook/useThemeManager';
import { theme } from '../../../../constants/theme';
import { FONT_ENUM } from '../../../../types/global/fonts.types';
import { FullScreenMoreMenu } from '../../../More/FullScreenMoreMenu';
import { IManagedEventDiscountCode } from '../../../../redux/events/types';

interface DiscountCardProps {
    discount: IManagedEventDiscountCode;
    onMoreAction: (actionType: string, discountId: string) => void;
}

function isDateExpired(date: string) {
    const targetDate = new Date(date);
    return targetDate.getTime() > Date.now();
}

export const DiscountCard: React.FC<DiscountCardProps> = ({ discount, onMoreAction }) => {
    const { themeStyle } = useThemeContext();
    const isActive = isDateExpired(discount.expiration_date) && discount.active;

    const moreMenuOptions = [
        { id: 'edit', title: 'Edit Discount' },
        { id: isActive ? 'deactivate' : 'activate', title: isActive ? 'Deactivate' : 'Activate' },
        { id: 'delete', title: 'Delete' },
    ];

    return (
        <Paper
            elevation={0}
            sx={{
                backgroundColor: theme[themeStyle].table_row,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme[themeStyle].placeholderGray}`,
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}>
            <Box
                sx={{
                    backgroundColor: theme[themeStyle].table_header,
                    py: 1.5,
                    px: 2,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                }}>
                <Typography
                    sx={{
                        fontFamily: FONT_ENUM.MAIN_BOLD,
                        fontSize: '1rem',
                        color: theme[themeStyle].primary_orange,
                        textTransform: 'uppercase',
                    }}>
                    {discount.code}
                </Typography>
                <Box
                    sx={{
                        backgroundColor: isActive ? theme[themeStyle].primaryGreen : theme[themeStyle].primary_red,
                        borderRadius: '2px',
                        px: 2,
                        py: 0.5,
                        display: 'inline-block',
                    }}>
                    <Typography
                        sx={{
                            fontSize: '0.65rem',
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            color: theme[themeStyle].primary_white,
                            textTransform: 'uppercase',
                        }}>
                        {isActive ? 'Active' : 'Inactive'}
                    </Typography>
                </Box>
            </Box>

            <Box sx={{ p: 2, flexGrow: 1 }}>
                <Box sx={{ mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '0.75rem',
                            color: theme[themeStyle].light_gray_text,
                            mb: 0.5,
                        }}>
                        Discount Amount
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1.25rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        {discount.discount}%
                    </Typography>
                </Box>

                <Box sx={{ mb: 2 }}>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '0.75rem',
                            color: theme[themeStyle].light_gray_text,
                            mb: 0.5,
                        }}>
                        Usage
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        {discount.count ?? 0}
                    </Typography>
                </Box>

                <Box>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_REGULAR,
                            fontSize: '0.75rem',
                            color: theme[themeStyle].light_gray_text,
                            mb: 0.5,
                        }}>
                        Expires
                    </Typography>
                    <Typography
                        sx={{
                            fontFamily: FONT_ENUM.MAIN_BOLD,
                            fontSize: '1rem',
                            color: theme[themeStyle].primary_white,
                        }}>
                        {new Date(discount.expiration_date).toLocaleDateString()}
                    </Typography>
                </Box>
            </Box>

            <Box
                sx={{
                    borderTop: `1px solid ${theme[themeStyle].placeholderGray}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    p: 1,
                }}>
                <FullScreenMoreMenu
                    menu={moreMenuOptions}
                    onClick={actionType => onMoreAction(actionType, discount._id)}
                    title="Discount Options"
                />
            </Box>
        </Paper>
    );
};
